import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

export interface FeedbackErrorContext {
 message: string;
 stack?: string;
 componentStack?: string;
}

export type FeedbackOpenSource = "user" | "error_boundary";

export interface FeedbackOpenOptions {
 source?: FeedbackOpenSource;
 error?: FeedbackErrorContext;
 autoCaptureScreenshot?: boolean;
}

export interface FeedbackContextValue {
 isOpen: boolean;
 openFeedback: (options?: FeedbackOpenOptions) => void;
 closeFeedback: () => void;
 openOptions: FeedbackOpenOptions | null;
}

const FeedbackContext = createContext<FeedbackContextValue | null>(null);

let globalOpenFeedback: ((options?: FeedbackOpenOptions) => void) | null = null;

/**
 * Imperative API for non-hook callers (e.g. class components like ErrorBoundary).
 */
export function openFeedback(options?: FeedbackOpenOptions) {
 globalOpenFeedback?.(options);
}

export function FeedbackProvider({ children }: { children: React.ReactNode }) {
 const [isOpen, setIsOpen] = useState(false);
 const [openOptions, setOpenOptions] = useState<FeedbackOpenOptions | null>(null);

 const openFeedbackImpl = (options?: FeedbackOpenOptions) => {
 setOpenOptions(options ?? { source: "user" });
 setIsOpen(true);
 };

 const closeFeedback = () => {
 setIsOpen(false);
 setOpenOptions(null);
 };

 useEffect(() => {
 globalOpenFeedback = openFeedbackImpl;
 return () => {
 if (globalOpenFeedback === openFeedbackImpl) {
 globalOpenFeedback = null;
 }
 };
 }, []);

 const value = useMemo<FeedbackContextValue>(
 () => ({
 isOpen,
 openFeedback: openFeedbackImpl,
 closeFeedback,
 openOptions,
 }),
 [isOpen, openOptions],
 );

 return <FeedbackContext.Provider value={value}>{children}</FeedbackContext.Provider>;
}

export function useFeedbackContext(): FeedbackContextValue {
 const ctx = useContext(FeedbackContext);
 if (!ctx) {
 throw new Error("useFeedbackContext must be used within FeedbackProvider");
 }
 return ctx;
}
